import { CodeHighlightNode, CodeNode } from '@lexical/code'
import { AutoLinkNode, LinkNode } from '@lexical/link'
import { ListItemNode, ListNode } from '@lexical/list'
import { TRANSFORMERS } from '@lexical/markdown'
import { createLinkMatcherWithRegExp } from '@lexical/react/LexicalAutoLinkPlugin'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { HeadingNode, QuoteNode } from '@lexical/rich-text'
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table'
import classNames from 'classnames'
import Button from 'ui/components/buttons/Button'
import {
  InputWrap,
  InputLabel,
  InputError,
} from 'ui/components/forms/Input/styled'
import AutoLinkPlugin from './plugins/AutoLinkPlugin'
import CodeHighlightPlugin from './plugins/CodeHighlightPlugin'
import LoadInitialContent from './plugins/LoadInitialContent'
import OnChangePlugin from './plugins/OnChangePlugin'
import ToolbarPlugin from './plugins/ToolbarPlugin'
import { LabelWrap, TemplatesHintButton, EditorWrap } from './styled'
import baseTheme from './themes/BaseTheme'

const editorConfig = {
  theme: baseTheme,
  nodes: [
    HeadingNode,
    ListNode,
    ListItemNode,
    QuoteNode,
    CodeNode,
    CodeHighlightNode,
    TableNode,
    TableCellNode,
    TableRowNode,
    AutoLinkNode,
    LinkNode,
  ],
  onError(error) {
    throw error
  },
}

const URL_REGEX =
  /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/
const EMAIL_REGEX =
  /(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/

export const Editor = ({
  meta,
  label,
  value,
  showTemplatesHintTrigger,
  onTemplatesHintTriggerClick,
  onChange,
  required,
  placeholder = 'Введите текст',
}) => {
  const isTouched = meta && meta.touched
  const hasError = isTouched && meta.error

  const MATCHERS = [
    createLinkMatcherWithRegExp(URL_REGEX, (text) => {
      return text
    }),
    createLinkMatcherWithRegExp(EMAIL_REGEX, (text) => {
      return `mailto:${text}`
    }),
  ]

  return (
    <InputWrap
      as={'div'}
      className={classNames({
        'has-error': hasError,
      })}
    >
      {(label || showTemplatesHintTrigger) && (
        <LabelWrap>
          {label && (
            <InputLabel>
              {label}
              {required && <span> *</span>}
            </InputLabel>
          )}
          {showTemplatesHintTrigger && (
            <TemplatesHintButton>
              <Button
                title="Выбрать шаблон"
                type="button"
                theme="borderNone"
                onClick={onTemplatesHintTriggerClick}
              />
            </TemplatesHintButton>
          )}
        </LabelWrap>
      )}

      <EditorWrap>
        <LexicalComposer initialConfig={editorConfig}>
          <div className="editor-container">
            <ToolbarPlugin />
            <div className="editor-inner">
              <RichTextPlugin
                contentEditable={<ContentEditable className="editor-input" />}
                placeholder={
                  <div className="editor-placeholder">{placeholder}</div>
                }
                ErrorBoundary={LexicalErrorBoundary}
              />
              <HistoryPlugin />
              <ListPlugin />
              <LinkPlugin />
              <AutoLinkPlugin matchers={MATCHERS} />
              <CodeHighlightPlugin />
              <LoadInitialContent initValue={value} />
              <OnChangePlugin onChange={onChange} />
              <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
            </div>
          </div>
        </LexicalComposer>
      </EditorWrap>
      {hasError && <InputError>{meta.error}</InputError>}
    </InputWrap>
  )
}
